import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { safeReturnTo } from '@/helpers/sessionPublicPath';
import { useTranslation } from 'react-i18next';
import useAuthContext from '@/contexts/Auth/useAuthContext';
import { getLoginErrorMessage, getLoginFailureReason } from '@/helpers/loginErrorHelper';
import { clearSessionTimedOutNotice, consumeSessionTimedOutFlag } from '@/helpers/sessionTimeout';
import { ANALYTICS_EVENTS } from '@/helpers/analyticsEvents';
import { usePostHogIdentification } from '@/hooks/usePostHogIdentification';
import Input from '@/components/ui/Input';
import PasswordInput from '@/components/ui/PasswordInput';
import Checkbox from '@/components/ui/Checkbox/Checkbox';
import Button from '@/components/ui/Button';
import Link from '@/components/ui/Link';
import Alert, { AlertType } from '@/components/ui/Alert';
import styles from './AuthPages.module.scss';

const LoginComponent = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { currentUser, login } = useAuthContext();
  const { captureEvent } = usePostHogIdentification();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [rememberMe, setRememberMe] = useState(false);
  const [errors, setErrors] = useState<{ email?: string; password?: string }>({});
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [alertMessage, setAlertMessage] = useState<{ message: string; type: AlertType }>(() =>
    consumeSessionTimedOutFlag()
      ? { message: t('sessionTimeout.expiredNotice'), type: 'info' }
      : { message: '', type: 'info' }
  );

  // StrictMode מרנדר שוב עם state ריק — ה-latch במודול נשאר עד התחברות.
  useEffect(() => {
    if (consumeSessionTimedOutFlag()) {
      setAlertMessage({ message: t('sessionTimeout.expiredNotice'), type: 'info' });
    }
  }, [t]);

  const validateForm = () => {
    const newErrors: { email?: string; password?: string } = {};
    if (!formData.email) {
      newErrors.email = t('email') + ' ' + t('required');
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = t('email') + ' ' + t('invalid');
    }
    if (!formData.password) {
      newErrors.password = t('password') + ' ' + t('required');
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitted(true);
    setAlertMessage({ message: '', type: 'info' });

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    captureEvent(ANALYTICS_EVENTS.LOGIN_ATTEMPTED);

    try {
      await login(formData.email, formData.password, rememberMe);
      clearSessionTimedOutNotice();
      captureEvent(ANALYTICS_EVENTS.LOGIN_SUCCEEDED);
      // HAT-1395: back to the page the session ended on - a link from a mail, most often.
      navigate(safeReturnTo(searchParams.get('returnTo')) ?? '/', { replace: true });
    } catch (error: unknown) {
      captureEvent(ANALYTICS_EVENTS.LOGIN_FAILED, { reason: getLoginFailureReason(error) });
      setAlertMessage({ message: getLoginErrorMessage(error, t), type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.formContainer}>
      {!currentUser && (
        <form onSubmit={handleSubmit} className={styles.form} aria-labelledby="login-heading">
          {/*
            כותרת h1 לעמוד. עמוד ההתחברות הוא נקודת הכניסה הציבורית לאזור
            האישי, וללא כותרת ראשית קורא מסך לא מקבל שום מושג היכן הוא נמצא.
            sr-only כדי לא לשנות את העיצוב הקיים.
          */}
          <h1 id="login-heading" className="sr-only">
            {t('login')}
          </h1>

          {alertMessage.message && <Alert type={alertMessage.type} message={alertMessage.message} />}

          <div className={styles.field}>
            <Input
              type="email"
              name="email"
              placeholder={t('email')}
              aria-label={t('email')}
              value={formData.email}
              onChange={handleChange}
              error={submitted && !!errors.email}
              aria-describedby={submitted && errors.email ? 'login-email-error' : undefined}
              autoComplete="email"
              autoFocus
            />
            {/* role="alert" - שגיאת ולידציה שמופיעה אחרי שליחה חייבת להיות
                מוקראת, לא רק מוצגת (WCAG 3.3.1). */}
            {submitted && errors.email && (
              <p id="login-email-error" role="alert" className={`${styles.fieldError} body-sm-regular`}>
                {errors.email}
              </p>
            )}
          </div>

          <div className={styles.field}>
            <PasswordInput
              name="password"
              placeholder={t('password')}
              aria-label={t('password')}
              value={formData.password}
              onChange={handleChange}
              error={submitted && !!errors.password}
              aria-describedby={submitted && errors.password ? 'login-password-error' : undefined}
              autoComplete="current-password"
            />
            {submitted && errors.password && (
              <p id="login-password-error" role="alert" className={`${styles.fieldError} body-sm-regular`}>
                {errors.password}
              </p>
            )}
          </div>

          <div className={styles.rememberMeRow}>
            <Checkbox
              checked={rememberMe}
              onCheckedChange={setRememberMe}
              ariaLabelledBy="login-remember-me-label"
            />
            <span
              id="login-remember-me-label"
              className={`body-sm-regular ${styles.rememberMeLabel}`}
              onClick={() => setRememberMe((value) => !value)}
            >
              {t('sessionTimeout.rememberMe')}
            </span>
          </div>

          <div className={styles.buttonsContainer}>
            <Button type="submit" variant="primary" disabled={loading}>
              {loading ? (
                <>
                  <span className={styles.spinner}></span> {t('connecting')}
                </>
              ) : (
                t('login')
              )}
            </Button>

            <Link to="/register"> {t('signup')}</Link>
            <Link to="/forgot-password"> {t('forgotPassword')} </Link>
          </div>
        </form>
      )}
    </div>
  );
};

export default LoginComponent;
